//inferenceUtils.js
import axios from "axios";
import { getToken } from "./auth";
import { API_BASE_URL } from "./crudFunctions";

const api = axios.create({
  baseURL: API_BASE_URL,
});

// Add request interceptor to inject fresh token for every request
api.interceptors.request.use(async (config) => {
  const token = await getToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});


// ---------------------------------------------------------------------
//  MODEL & INFERENCE HELPERS
// ---------------------------------------------------------------------

/**
 * Upload a YOLOv8 .pt model for a given project.
 *
 * @param {string} projectName   Name of the project folder on the server
 * @param {File|Blob} modelFile  The .pt file selected in an <input type="file">
 * @param {string[]} [classes]   Optional list of class names—will be stored in config.yaml
 * @returns {Promise<object>}    Server response with {status, message, model_version, model_path}
 */
export async function uploadModel(projectName, modelFile, classes = []) {
  try {
    const formData = new FormData();
    formData.append("model", modelFile);
    if (classes.length) formData.append("classes", classes.join(","));

    const { data } = await api.post(
      `/projects/${encodeURIComponent(projectName)}/upload_model`,
      formData,
      { headers: { "Content-Type": "multipart/form-data" } }
    );
    return data; // { status: "success", ... }
  } catch (err) {
    // Forward server JSON if available, otherwise throw raw error
    throw err?.response?.data || err;
  }
}

/**
 * Fetch every stored model version and its config.
 *
 * @returns {Promise<Array<{version: string, config: object}>>}
 */
export async function getAllModels() {
  try {
    const { data } = await api.get("/models");
    return data.models; // normalised list
  } catch (err) {
    throw err?.response?.data || err;
  }
}

/**
 * Run inference on one image inside a project with a chosen model.
 *
 * @param {string}  projectName  Project folder
 * @param {number}  imageIndex   Zero-based index in the images/ directory
 * @param {string}  modelPath    Absolute/relative path returned by uploadModel()
 * @returns {Promise<object>}    { status, image_name, predictions }
 */
export async function predictImage(projectName, imageIndex, modelPath) {
  try {
    const payload = {
      image_index: imageIndex,
      model_path: modelPath,
    };

    const { data } = await api.post(
      `/projects/${encodeURIComponent(projectName)}/predict`,
      payload
    );
    return data;
  } catch (err) {
    throw err?.response?.data || err;
  }
}

export const fetchInferenceResults = async (projectName, imageId) => {
  try {
    const response = await api.get(`/projects/${projectName}/infer/${imageId}`);

    console.log('Image inference fetched:', response.data);
    return response.data;
  } catch (error) {
    console.error('Error fetching results:', error);
    throw error;
  }
};

/**
 * Run direct inference on one image inside a project using user-provided points.
 * @param {string} projectName Project folder
 * @param {number|string} imageId Image index or ID
 * @param {Array} points Array of [x, y] points inside the object
 * @returns {Promise<object>} { status, inference }
 */
export const fetchDirectInferenceResults = async (projectName, imageId, point) => {
  try {
    const payload = {
      image_id: imageId,
      point
    };
    console.log('payload', payload);
    const response = await api.post(`/projects/${projectName}/hover_sam_infer`, payload);
    return response.data;
  } catch (error) {
    console.error('Error fetching direct inference results:', error);
    throw error;
  }
};

export const sendInclusionExclutionPoints = async (projectName, imageId, inclusion_points, exclusion_points) => {
  try {
    const payload = {
      image_id: imageId,
      inclusion_points, // Array of [x, y] points to include
      exclusion_points, // Array of [x, y] points to exclude
    };
    const response = await api.post(`/projects/${projectName}/sam_infer_2`, payload);
    return response.data;
  } catch (error) {
    console.error('Error sending inclusion points:', error);
    throw error;
  }
}

